import { ChartLegendWrapper, Barchart as CoreUIBarChart } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useBarChartParams } from '../../../hooks/useBarChartParams';
import { ChartBody, ChartCard, ChartHeaderActions } from '../../chat-messages/ChartWrapper';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { ResolveInstanceMarkers } from '../../ResolveInstanceMarkers';
import { NoData } from '../NoData';
import { ChartLegendPanel } from '../shared/ChartLegendPanel';
import { buildCategoryTable } from '../shared/chartExportTables';

const BAR_CHART_TYPE = { type: 'category' } as const;

type BarChartProps = {
  title: string;
  yAxisTitle: string;
  xAxisTitle?: string;
  xAxisLabels: string[];
  dataset: { label: string; data: number[] }[];
  yAxisScale?: 'linear' | 'log';
};

/** What the bars are counted by, when the payload does not name it. */
const DEFAULT_X_AXIS_TITLE = 'label';

const BarChartView: React.FC<BarChartProps> = ({
  title,
  yAxisTitle,
  xAxisTitle,
  xAxisLabels,
  dataset,
  yAxisScale,
}) => {
  const { bars, colorSet } = useBarChartParams({ xAxisLabels, dataset });

  if (xAxisLabels.length === 0 || !dataset.some((set) => set.data.length > 0)) {
    return (
      <ChartCard>
        <NoData title={title} />
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title={title}
      buildExportTable={() =>
        buildCategoryTable({
          name: title,
          categoryColumn: xAxisTitle ?? DEFAULT_X_AXIS_TITLE,
          categories: xAxisLabels,
          series: dataset.map((set) => ({ label: set.label, values: set.data })),
        })
      }
    >
      {(view) => (
        <ChartLegendWrapper colorSet={colorSet}>
          <ChartBody legend={<ChartLegendPanel />}>
            <CoreUIBarChart
              type={BAR_CHART_TYPE}
              title={title}
              rightTitle={<ChartHeaderActions actions={view.actions} caption={yAxisTitle} />}
              bars={bars}
              height={view.chartHeight}
              yAxisScale={yAxisScale ?? 'linear'}
            />
          </ChartBody>
        </ChartLegendWrapper>
      )}
    </ChartCard>
  );
};

export const BarChart: AIToolComponent<BarChartProps> = ({ params }) => (
  <ResolveInstanceMarkers
    value={params.xAxisLabels}
    render={(xAxisLabels) => <BarChartView {...params} xAxisLabels={xAxisLabels} />}
  />
);

BarChart.description = 'Displays a bar chart to display categorized data points.';

BarChart.schema = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    yAxisTitle: { type: 'string' },
    xAxisTitle: {
      type: 'string',
      description: "What the bars are counted by, e.g. 'status', 'node'. Names the X axis in an export.",
    },
    xAxisLabels: { type: 'array', items: { type: 'string' }, minItems: 2 },
    yAxisScale: {
      type: 'string',
      enum: ['linear', 'log'],
      description:
        "Y-axis scale. 'log' gives each decade the same height, for values spanning orders of magnitude where a linear axis flattens the small ones onto the baseline. Display only: no value is rescaled.",
      default: 'linear',
    },
    dataset: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        properties: {
          label: { type: 'string' },
          data: { type: 'array', items: { type: 'number' }, minItems: 2 },
        },
        required: ['label', 'data'],
      },
    },
  },
  required: ['title', 'yAxisTitle', 'xAxisLabels', 'dataset'],
};

BarChart.displayInPanel = true;
